'use client';

import { useState, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Images, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Typography } from '@/shared/ui/shadcn/typography';
import { LoadingSpinner, EmptyState } from '@/shared/ui';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { isProjectAngleValid, type ProjectAngle } from '@/types/project-angle';
import type { ConceptAngle } from '@/types/concept';
import { cn } from '@/shared/lib/cn';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { toast } from '@/shared/lib/toast';

interface ReplaceAnglePickerProps {
  projectId: string;
  conceptId: string;
  conceptAngleId: string;
  conceptAngles: ConceptAngle[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (selectedAngleId: string) => Promise<void>;
}

export function ReplaceAnglePicker({
  projectId,
  conceptId,
  conceptAngleId: _conceptAngleId,
  conceptAngles,
  open,
  onOpenChange,
  onSelect,
}: ReplaceAnglePickerProps) {
  const t = useTranslations('angle.replace');
  const tCommon = useTranslations('common');

  // Data
  const { data: allAngles = [], isLoading: isLoadingAngles } =
    useProjectAngles(projectId);

  const queryClient = useQueryClient();

  // State
  const [isSelecting, setIsSelecting] = useState(false);

  // Filter to valid angles not already in the concept
  const availableAngles = useMemo(() => {
    const conceptAngleIds = new Set(
      conceptAngles
        .map((ca) => ca.relationships?.angle?.data?.id)
        .filter(Boolean)
    );
    return allAngles
      .filter(isProjectAngleValid)
      .filter((a) => !conceptAngleIds.has(a.id));
  }, [allAngles, conceptAngles]);

  // Handle angle selection
  const handleSelect = useCallback(
    async (angleId: string) => {
      if (isSelecting) return;

      setIsSelecting(true);
      try {
        await onSelect(angleId);
        onOpenChange(false);
      } catch (err) {
        // The swap used to fail silently: the picker just stopped spinning.
        // Show the API's own words (409 when the view is already in the
        // design, 404 when either side is gone); a 4xx also means our copy of
        // the design is out of date, so reload it.
        console.error('Failed to swap angle:', err);
        toast.error(err instanceof ApiClientError ? err.message : tCommon('error'));
        if (err instanceof ApiClientError && err.status >= 400 && err.status < 500) {
          void queryClient.invalidateQueries({
            queryKey: cacheKeys.concepts.detail(conceptId),
          });
        }
      } finally {
        setIsSelecting(false);
      }
    },
    [isSelecting, onSelect, onOpenChange, tCommon, queryClient, conceptId]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('selectExisting')}</DialogTitle>
        </DialogHeader>

        {isLoadingAngles ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : availableAngles.length === 0 ? (
          <EmptyState
            icon={<Images />}
            title={t('noAvailableAngles')}
            description={t('noAvailableAnglesHint')}
            className="flex-1"
          />
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
            <div className="space-y-1">
              {availableAngles.map((angle) => (
                <AnglePickerRow
                  key={angle.id}
                  angle={angle}
                  disabled={isSelecting}
                  onSelect={() => handleSelect(angle.id)}
                />
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSelecting}
          >
            {tCommon('cancel')}
          </Button>
        </DialogFooter>

        {/* Loading overlay */}
        {isSelecting && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center rounded-lg">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner size="lg" />
              <Typography variant="small" className="text-muted-foreground">
                {t('replacing')}
              </Typography>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface AnglePickerRowProps {
  angle: ProjectAngle;
  disabled: boolean;
  onSelect: () => void;
}

function AnglePickerRow({ angle, disabled, onSelect }: AnglePickerRowProps) {
  const displayName =
    angle.attributes.name || `Angle ${angle.attributes.order + 1}`;

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2 px-2 rounded-lg transition-all cursor-pointer',
        'hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={() => !disabled && onSelect()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Thumbnail */}
      <div
        className={cn(
          'relative w-[56px] h-[56px] flex-shrink-0 rounded-md overflow-hidden bg-muted',
          disabled && 'opacity-50'
        )}
      >
        {angle.attributes.file_url ? (
          <img
            src={angle.attributes.file_url}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <span className="text-card-meta">No image</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={cn('flex-1 min-w-0', disabled && 'opacity-50')}>
        <Typography variant="p" className="font-medium truncate !mt-0">
          {displayName}
        </Typography>
        {angle.attributes.description && (
          <Typography
            variant="small"
            className="text-muted-foreground truncate mt-0.5"
          >
            {angle.attributes.description.replace(/\n/g, ' ')}
          </Typography>
        )}
      </div>

      {/* Arrow icon */}
      <ArrowRight className="w-5 h-5 text-muted-foreground rtl:-scale-x-100" />
    </div>
  );
}
